import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  validateResetToken, resetPassword, adminLogin,
} from "../lib/adminApi";

/**
 * Branded activation page for first-time client portal access.
 * Route: /transformation/activate/:token
 *
 * Flow:
 *   1. Validate the invite token (reuses the existing /auth/reset-token endpoint).
 *   2. Show "Set Your Password" form.
 *   3. On submit → reset password → auto-login → redirect to the client dashboard.
 *   4. Expired / invalid tokens render the dedicated "no longer valid" state.
 */
export default function ClientActivation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tokenErr, setTokenErr] = useState("");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    validateResetToken(token)
      .then((data) => { setInfo(data); setChecking(false); })
      .catch((e) => {
        setTokenErr(e?.response?.data?.detail || "Link is invalid or has expired");
        setChecking(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Persist the new password (marks `pending_password=false`).
      await resetPassword(token, password);
      // 2) Auto-login using the freshly set credentials so the client
      //    lands inside the dashboard without retyping them.
      await adminLogin(info.email, password);
      // 3) Client Access invitees go to My Evaluations; legacy portal invitees
      //    keep the existing Client Portal dashboard.
      let dest = "/transformation/dashboard";
      try {
        const { resolveClientLanding } = await import("../lib/clientAccess");
        const landing = await resolveClientLanding();
        if (landing) dest = landing;
      } catch { /* fall back to legacy dashboard */ }
      navigate(dest, { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header variant="solid" />
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8">
            {checking ? (
              <p
                className="text-sm text-[#6B7280] text-center"
                data-testid="activation-checking"
              >
                Verifying your access link…
              </p>
            ) : tokenErr ? (
              <div
                data-testid="activation-invalid"
                className="text-center"
              >
                <div
                  className="mx-auto w-12 h-12 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: "#FEE2E2", color: "#DC2626" }}
                >
                  <AlertCircle className="w-6 h-6" strokeWidth={2.25} />
                </div>
                <h1
                  className="mt-5 text-xl font-bold text-[#0F172A] tracking-tight"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                  data-testid="activation-invalid-title"
                >
                  This access link is no longer valid
                </h1>
                <p
                  className="mt-2 text-sm text-[#4B5563] leading-relaxed"
                  data-testid="activation-invalid-subtitle"
                >
                  Please contact your consultant or request a new access link.
                </p>
                <Button
                  data-testid="activation-invalid-back"
                  onClick={() => navigate("/transformation")}
                  variant="outline"
                  className="mt-6 rounded-sm h-9 px-4 text-xs font-semibold border-[#E5E7EB] text-[#0F172A] hover:bg-[#F1F5F9]"
                >
                  Back to sign in
                </Button>
              </div>
            ) : (
              <>
                <div
                  className="w-10 h-10 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: "#E0F2FE", color: "#025979" }}
                >
                  <Lock className="w-5 h-5" strokeWidth={2.25} />
                </div>
                <h1
                  className="mt-4 text-2xl font-bold text-[#0F172A] tracking-tight"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                  data-testid="activation-title"
                >
                  Set Your Password
                </h1>
                <p
                  className="mt-1.5 text-sm text-[#4B5563] leading-relaxed"
                  data-testid="activation-subtitle"
                >
                  Create your password to access your WarehouseX Transformation Dashboard.
                </p>
                <p className="mt-3 text-xs text-[#6B7280]">
                  Activating access for{" "}
                  <span
                    className="font-semibold text-[#0F172A]"
                    data-testid="activation-email"
                  >
                    {info?.email}
                  </span>
                </p>

                <form onSubmit={submit} className="mt-5 space-y-4">
                  <div>
                    <label
                      htmlFor="activation-password"
                      className="text-xs font-semibold text-[#111827] block mb-1"
                    >
                      Password
                    </label>
                    <Input
                      id="activation-password"
                      data-testid="activation-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-sm text-sm"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="activation-confirm"
                      className="text-xs font-semibold text-[#111827] block mb-1"
                    >
                      Confirm Password
                    </label>
                    <Input
                      id="activation-confirm"
                      data-testid="activation-confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="rounded-sm text-sm"
                      placeholder="Re-enter your password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  {err && (
                    <p
                      data-testid="activation-error"
                      className="text-xs text-red-600"
                    >
                      {err}
                    </p>
                  )}
                  <Button
                    type="submit"
                    data-testid="activation-submit"
                    disabled={submitting}
                    className="w-full bg-[#025979] hover:bg-[#024a63] text-white rounded-sm text-sm h-10 font-semibold"
                  >
                    {submitting ? "Activating…" : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Activate Account
                      </>
                    )}
                  </Button>
                  <div className="flex items-start gap-1.5 text-[10px] text-[#6B7280] leading-snug">
                    <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-[#025979]" />
                    <p>
                      Minimum 8 characters. Your password is encrypted and never
                      shared with your consultant. You can change it anytime.
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
